/**
 * Paket yönetimi — admin tanımlı paketler hareketler'de (vt:'FikoYT.sistem.fiyat.paketler') saklanır.
 * Public + sihirbaz aktif paketleri buradan okur; DB boşsa kod varsayılanları (ROL_PAKETLERI) kullanılır.
 */
import { Injectable, inject } from '@angular/core';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { IPaket, ROL_PAKETLERI, IErisimAyar } from './paketler';

const VT = 'FikoYT.sistem.fiyat.paketler';
const VT_AYAR = 'FikoYT.sistem.fiyat.ayarlar';

@Injectable({ providedIn: 'root' })
export class PaketService {
  private req = inject(FikoYTRequestService);

  /** DB paketleri (yoksa boş). `sadeceAktif` true → public görünümü. */
  async dbPaketleri(sadeceAktif = false): Promise<IPaket[]> {
    const match: any = { vt: VT, silindi: { $ne: true } };
    if (sadeceAktif) match.aktif = { $ne: false };
    const r = await this.req.aggregate<any>('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: match }, { $sort: { sira: 1, olusturulmaTarihi: 1 } },
    ]);
    return (r || []).map(d => this.normalize(d));
  }

  /** Kod varsayılanlarının id listesi (silinemez, sadece düzenlenir/gizlenir). */
  readonly varsayilanIdler = new Set(ROL_PAKETLERI.map(p => p.id));

  /**
   * Varsayılan + DB paketleri `id` üzerinden birleştir.
   * DB'de aynı id varsa varsayılanı override eder (düzenlenmiş hali). DB-only id'ler özel pakettir.
   */
  async tumPaketler(sadeceAktif = false): Promise<IPaket[]> {
    const db = await this.dbPaketleri(false);
    const dbById = new Map(db.map(p => [p.id, p]));
    const birlesik: IPaket[] = [];
    ROL_PAKETLERI.forEach((d, i) => birlesik.push(dbById.get(d.id) || { ...d, aktif: true, sira: i }));
    for (const p of db) if (!this.varsayilanIdler.has(p.id)) birlesik.push(p);
    birlesik.sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
    return sadeceAktif ? birlesik.filter(p => p.aktif !== false) : birlesik;
  }

  /** Sihirbaz/public için: aktif (varsayılan + DB) paketler. */
  async gosterilecekPaketler(sadeceAktif = true): Promise<IPaket[]> {
    return this.tumPaketler(sadeceAktif);
  }

  async kaydet(p: IPaket): Promise<any> {
    const veri = {
      id: p.id, ad: p.ad, simge: p.simge, renk: p.renk, aciklama: p.aciklama,
      rozet: p.rozet || '', indirimYuzde: p.indirimYuzde, secim: p.secim,
      fiyatlar: p.fiyatlar || {}, aktif: p.aktif !== false, sira: p.sira ?? 0,
    };
    if (p._id) {
      return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle', { _id: p._id }, { $set: veri });
    }
    return this.req.ekle('FikoYT.sistem.DB.hareketler.ekle', { vt: VT, ...veri, olusturulmaTarihi: new Date().toISOString() });
  }

  async sil(id: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle', { _id: id }, { $set: { silindi: true } });
  }

  // ── SİSTEM FİYAT AYARLARI (tek doc: global fiyatlar + erişim ayarları) ──
  private _ayarId = '';
  /** Ayar doc'u: global kalem fiyatları + erişim seviyeleri (kampanya/ücretsiz) + 📢 baz erişim zorunluluğu. */
  async ayarlar(): Promise<{ fiyatlar: Record<string, number>; erisimSeviyeleri: Record<string, IErisimAyar>; paylasimBazZorunlu: boolean }> {
    const r = await this.req.aggregate<any>('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: VT_AYAR } }, { $sort: { olusturulmaTarihi: -1 } }, { $limit: 1 },
    ]);
    this._ayarId = r?.[0]?._id || '';
    return { fiyatlar: r?.[0]?.fiyatlar || {}, erisimSeviyeleri: r?.[0]?.erisimSeviyeleri || {}, paylasimBazZorunlu: !!r?.[0]?.paylasimBazZorunlu };
  }
  async erisimSeviyeleri(): Promise<Record<string, IErisimAyar>> { return (await this.ayarlar()).erisimSeviyeleri; }
  async erisimSeviyeKaydet(erisimSeviyeleri: Record<string, IErisimAyar>): Promise<any> { return this.ayarGuncelle({ erisimSeviyeleri }); }
  /** Ayar doc'una kısmi güncelleme (upsert). */
  async ayarGuncelle(patch: Record<string, any>): Promise<any> {
    if (this._ayarId) return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle', { _id: this._ayarId }, { $set: patch });
    const r = await this.req.ekle<any>('FikoYT.sistem.DB.hareketler.ekle', { vt: VT_AYAR, ...patch, olusturulmaTarihi: new Date().toISOString() });
    this._ayarId = r?._id || r?.insertedId || '';
    return r;
  }
  /** Global fiyat override haritası (anahtar→₺). Kod varsayılanını ezer; pakete/kullanıcıya özel bunu ezer. */
  async globalFiyatlar(): Promise<Record<string, number>> { return (await this.ayarlar()).fiyatlar; }
  async globalFiyatKaydet(fiyatlar: Record<string, number>): Promise<any> { return this.ayarGuncelle({ fiyatlar }); }

  private normalize(d: any): IPaket {
    return {
      _id: d._id, id: d.id || d._id, ad: d.ad || '', simge: d.simge || '📦', renk: d.renk || '#6366f1',
      aciklama: d.aciklama || '', rozet: d.rozet || undefined, indirimYuzde: +d.indirimYuzde || 0,
      fiyatlar: d.fiyatlar || {}, aktif: d.aktif !== false, sira: d.sira ?? 0,
      secim: { uyg: d.secim?.uyg || [], alt: d.secim?.alt || [], wb: d.secim?.wb || [], km: d.secim?.km || [] },
    };
  }
}
